<script setup lang="ts">
import { computed } from 'vue'

export type ToolType = 'select' | 'addNode' | 'addEdge' | 'deleteNode' | 'deleteEdge'
export type ToolbarTheme = 'light' | 'dark'

interface Props {
  /** 当前工具（v-model） */
  activeTool?: ToolType
  /** 是否在编辑模式 */
  isEditing?: boolean
  /** 工具栏主题 */
  theme?: ToolbarTheme
}

const props = withDefaults(defineProps<Props>(), {
  activeTool: 'select',
  isEditing: false,
  theme: 'light'
})

const emit = defineEmits<{
  'update:activeTool': [tool: ToolType]
  'tool-change': [tool: ToolType]
}>()

const active = computed({
  get: () => props.activeTool,
  set: (v) => {
    emit('update:activeTool', v)
    emit('tool-change', v)
  }
})

const selectTool = (tool: ToolType) => {
  active.value = tool
}

// Tool descriptions for title tooltips
const toolLabels: Record<ToolType, string> = {
  select: '选择',
  addNode: '添加节点',
  addEdge: '创建链接',
  deleteNode: '删除节点',
  deleteEdge: '删除边'
}
</script>

<template>
  <div class="kg-editor-toolbar" :class="{ 'kg-theme-dark': theme === 'dark' }" v-if="isEditing">
    <!-- 选择工具 -->
    <button
      class="kg-tool-btn"
      :class="{ active: active === 'select' }"
      @click="selectTool('select')"
      :title="toolLabels.select"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 3l14 9-7 1-4 7z"/>
      </svg>
    </button>

    <div class="kg-toolbar-divider" />

    <!-- 添加节点 -->
    <button
      class="kg-tool-btn"
      :class="{ active: active === 'addNode' }"
      @click="selectTool('addNode')"
      :title="toolLabels.addNode"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <line x1="12" y1="8" x2="12" y2="16"/>
        <line x1="8" y1="12" x2="16" y2="12"/>
      </svg>
    </button>

    <!-- 创建链接 -->
    <button
      class="kg-tool-btn"
      :class="{ active: active === 'addEdge' }"
      @click="selectTool('addEdge')"
      :title="toolLabels.addEdge"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="5" cy="12" r="2.5"/>
        <circle cx="19" cy="12" r="2.5"/>
        <line x1="7.5" y1="12" x2="16.5" y2="12"/>
        <polyline points="13.5 9 16.5 12 13.5 15"/>
      </svg>
    </button>

    <div class="kg-toolbar-divider" />

    <!-- 删除节点 -->
    <button
      class="kg-tool-btn kg-tool-btn-danger"
      :class="{ active: active === 'deleteNode' }"
      @click="selectTool('deleteNode')"
      :title="toolLabels.deleteNode"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <line x1="12" y1="8" x2="12" y2="16"/>
      </svg>
    </button>

    <!-- 删除边 -->
    <button
      class="kg-tool-btn kg-tool-btn-danger"
      :class="{ active: active === 'deleteEdge' }"
      @click="selectTool('deleteEdge')"
      :title="toolLabels.deleteEdge"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="4" y1="12" x2="20" y2="12"/>
        <line x1="12" y1="4" x2="12" y2="20"/>
      </svg>
    </button>
  </div>
</template>

<style scoped>
.kg-editor-toolbar {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 6px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(200, 210, 220, 0.6);
  border-radius: 12px;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 1px 4px rgba(0, 0, 0, 0.04);
  user-select: none;
}

.kg-tool-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
  outline: none;
}

.kg-tool-btn svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.kg-tool-btn:hover {
  background: rgba(59, 130, 246, 0.08);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.2);
}

.kg-tool-btn.active {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

/* 危险操作按钮 */
.kg-tool-btn-danger:hover {
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.2);
}

.kg-tool-btn-danger.active {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border-color: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.15);
}

/* 分隔线 */
.kg-toolbar-divider {
  width: 24px;
  height: 1px;
  background: rgba(200, 210, 220, 0.6);
  margin: 2px 0;
}

.kg-editor-toolbar.kg-theme-dark {
  background: rgba(15, 23, 42, 0.92);
  border-color: rgba(71, 85, 105, 0.65);
  box-shadow:
    0 8px 24px rgba(2, 6, 23, 0.45),
    0 2px 8px rgba(2, 6, 23, 0.32);
}

.kg-editor-toolbar.kg-theme-dark .kg-tool-btn {
  color: #cbd5e1;
}

.kg-editor-toolbar.kg-theme-dark .kg-tool-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  color: #93c5fd;
  border-color: rgba(147, 197, 253, 0.38);
}

.kg-editor-toolbar.kg-theme-dark .kg-tool-btn.active {
  background: rgba(59, 130, 246, 0.26);
  color: #bfdbfe;
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.22);
}

.kg-editor-toolbar.kg-theme-dark .kg-tool-btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
  border-color: rgba(252, 165, 165, 0.36);
}

.kg-editor-toolbar.kg-theme-dark .kg-tool-btn-danger.active {
  background: rgba(239, 68, 68, 0.28);
  color: #fecaca;
  border-color: #f87171;
  box-shadow: 0 0 0 2px rgba(248, 113, 113, 0.24);
}

.kg-editor-toolbar.kg-theme-dark .kg-toolbar-divider {
  background: rgba(71, 85, 105, 0.75);
}
</style>
